import { Link, useLocation } from '@tanstack/react-router'
import { ChevronDownIcon } from 'lucide-react'

import {
  Card,
  DropdownMenuItem,
  PriorityNav,
  SubnavItem,
} from '@/components/ui'
import {
  useCheckTransactions,
  useFlaggedCheckFiles,
  useHasPortfolio,
  useProject,
} from '@/modules/projects'
import { cn, getProjectPath } from '@/utils'
import { ProcessingNotice } from './ProcessingNotice'

interface ProjectNavProps {
  slug: string
}

interface NavItem {
  label: string
  path: string
  exact?: boolean
  isNew?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Overview', path: '', exact: true },
  { label: 'Files', path: '/files' },
  { label: 'Transactions', path: '/transactions' },
  { label: 'Portfolio', path: '/portfolio', isNew: true },
  { label: 'Checks', path: '/checks', isNew: true },
  { label: 'Screening', path: '/screening', isNew: true },
  { label: 'Insights', path: '/insights' },
  { label: 'AI Analysis', path: '/analysis' },
]

const getItemPath = (slug: string, item: NavItem) =>
  `${getProjectPath({ slug })}${item.path}`

const NewBadge = () => (
  <span className="ml-1.5 rounded-full bg-emerald-500 px-1.5 py-0.5 font-semibold text-[9px] text-white uppercase tracking-wider">
    New
  </span>
)

export const ProjectNav = ({ slug }: ProjectNavProps) => {
  const location = useLocation()
  const hasCheckRows = useCheckTransactions().length > 0
  const hasFlaggedCheckFiles = useFlaggedCheckFiles().length > 0
  const hasPortfolio = useHasPortfolio()
  // `hasTransactions` rides on the project, which the layout always loads, so this stays steady on
  // every tab (unlike the transactions store, which only fills on tabs that fetch it).
  const hasTransactions = useProject()?.hasTransactions ?? false
  const hasScreening = useProject()?.hasScreening ?? false

  // Show Transactions once there are cash transactions, Checks once there are check images or a
  // flagged check file (one whose pages all failed has no rows but must not vanish), and Portfolio once securities exist, are still being read, or failed to read (so the tab says so
  // instead of popping in late or vanishing).
  const navItems = NAV_ITEMS.filter((item) => {
    if (item.path === '/transactions') return hasTransactions
    if (item.path === '/checks') return hasCheckRows || hasFlaggedCheckFiles
    if (item.path === '/portfolio') return hasPortfolio
    if (item.path === '/screening') return hasScreening
    return true
  })

  const isActive = (item: NavItem) => {
    const path = getItemPath(slug, item)
    if (!item.exact) return location.pathname.startsWith(path)

    return location.pathname === path || location.pathname === `${path}/`
  }

  const renderItem = (item: NavItem) => (
    <SubnavItem asChild active={isActive(item)}>
      <Link to={getItemPath(slug, item)} preload="intent">
        {item.label}
        {item.isNew && <NewBadge />}
      </Link>
    </SubnavItem>
  )

  const renderMenuItem = (item: NavItem) => (
    <DropdownMenuItem asChild>
      <Link
        to={getItemPath(slug, item)}
        preload="intent"
        className={cn(
          'cursor-pointer',
          isActive(item) && 'font-medium text-emerald-500',
        )}
      >
        {item.label}
        {item.isNew && <NewBadge />}
      </Link>
    </DropdownMenuItem>
  )

  const renderMoreTrigger = ({ active }: { active: boolean }) => (
    <button
      type="button"
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 font-medium text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        // Use an inset ring, not a border: a real border adds 1px top and bottom and gets clipped
        // by the row, while a ring is painted, not laid out, so the button stays the tabs' height.
        active
          ? 'bg-white text-emerald-500 ring-1 ring-border ring-inset'
          : 'text-blue-7 hover:text-blue-6',
        'data-[state=open]:text-emerald-500',
      )}
    >
      More
      <ChevronDownIcon className="size-3.5" />
    </button>
  )

  return (
    <div>
      <Card className="relative z-10 bg-muted">
        <PriorityNav
          items={navItems}
          getKey={(item) => item.path}
          isItemActive={isActive}
          renderItem={renderItem}
          renderMenuItem={renderMenuItem}
          renderMoreTrigger={renderMoreTrigger}
          className="h-10 p-1"
          gap={4}
        />
      </Card>
      <ProcessingNotice />
    </div>
  )
}
